"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Service = {
  number: string;
  title: string;
  descriptor: string;
  body: string;
  includes: string[];
  pricing: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const services: Service[] = [{
  number: "N°.01",
  title: "Ginecologia e Ostetricia",
  descriptor: "Assistenza clinica dedicata alla salute femminile in ogni fase della vita.",
  body: "Offro un percorso di cura personalizzato, basato su un approccio scientifico rigoroso e una profonda attenzione alla persona. Dalla prevenzione ginecologica alla gestione della gravidanza, ogni visita è finalizzata a garantire il massimo benessere e la serenità della paziente attraverso una diagnostica avanzata.",
  includes: ["Visite ginecologiche specialistiche", "Monitoraggio della gravidanza", "Prevenzione oncologica femminile", "Ecografia ostetrica e ginecologica", "Consulenza contraccettiva"],
  pricing: "CONSULENZA CLINICA",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_ostetricia.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_ostetricia.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_ostetricia.png-wvc-sizes",
  alt: "Studio medico ginecologico con attrezzature diagnostiche moderne"
}, {
  number: "N°.02",
  title: "Fisiopatologia della Riproduzione Umana",
  descriptor: "Supporto esperto per il percorso verso la genitorialità.",
  body: "La fisiopatologia della riproduzione umana richiede competenza e sensibilità. Analizzo le cause dell'infertilità di coppia con protocolli diagnostici mirati, offrendo un orientamento chiaro e supportato dalle più recenti evidenze scientifiche per affrontare il percorso di procreazione assistita.",
  includes: ["Diagnostica dell'infertilità di coppia", "Valutazione della riserva ovarica", "Percorsi di procreazione medicalmente assistita", "Supporto endocrinologico riproduttivo", "Consulenza pre-concezionale"],
  pricing: "PERCORSO SPECIALISTICO",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/riproduzione_umana.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/riproduzione_umana.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/riproduzione_umana.webp-wvc-sizes",
  alt: "Consulto medico professionale in ambiente accogliente"
}, {
  number: "N°.03",
  title: "Ginecologia Forense",
  descriptor: "Competenza medico-legale al servizio della giustizia e della tutela.",
  body: "La ginecologia forense rappresenta l'intersezione tra la pratica clinica e la responsabilità legale. Fornisco perizie tecniche e consulenze medico-legali in ambito ostetrico-ginecologico, garantendo imparzialità, rigore analitico e una profonda conoscenza della normativa vigente per la tutela dei diritti.",
  includes: ["Perizie medico-legali in ostetricia", "Valutazione del danno alla persona", "Consulenza tecnica di parte (CTP)", "Analisi della responsabilità professionale", "Pareri legali in ambito ginecologico"],
  pricing: "CONSULENZA FORENSE",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_forense.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_forense.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1724194/img/ginecologia_forense.webp-wvc-sizes",
  alt: "Documentazione medica e analisi forense in studio professionale"
}];
function ServiceBlock({
  service,
  index
}: {
  service: Service;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-center">
        <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
          <div className="aspect-[4/5] w-full overflow-hidden rounded-lg">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className={`lg:col-span-6 ${isReversed ? "lg:order-1 lg:pr-20" : "lg:order-2 lg:pl-20"}`}>
          <div className="max-w-xl">
            <p className="font-mono text-xs tracking-widest uppercase mb-6" style={{
            color: "var(--primary)"
          }}>
              {service.number}
            </p>
            <h3 className="font-serif font-default text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-5">
              {service.title}
            </h3>
            <p className="font-serif italic text-xl text-foreground/70 mb-8 leading-snug">
              {service.descriptor}
            </p>
            <p className="text-[17px] leading-relaxed mb-10" style={{
            color: "var(--foreground)",
            opacity: 0.8
          }}>
              {service.body}
            </p>

            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-5">
                Servizi inclusi
              </p>
              <ul className="space-y-3">
                {service.includes.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-[15px] text-foreground/85">
                    <span className="mt-2 inline-block h-1 w-1 rounded-lg shrink-0" style={{
                  backgroundColor: "var(--primary)"
                }} aria-hidden="true" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>

            <p className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-8">
              {service.pricing}
            </p>

            <Button asChild size="lg" className="rounded-lg px-7 h-12 text-sm tracking-wide bg-primary text-white hover:bg-primary/90">
              <Link to="/ContactsPage">
                Richiedi un consulto
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function Specializzazioni() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="794" id="ambiti-di-specializzazione" className="bg-background text-foreground pt-32 sm:pt-40 lg:pt-[200px] pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div ref={introRef} className={`text-center mb-24 lg:mb-32 transition-all duration-1000 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase mb-10" style={{
          color: "var(--primary)"
        }}><br /></p>
          <h2 className="font-serif font-default text-foreground leading-[0.95] mb-10" style={{
          fontSize: "clamp(4rem, 10vw, 8rem)"
        }}>
            Medicina,{" "}
            <span className="italic font-light text-foreground/85">con precisione.</span>
          </h2>
          <p className="mx-auto text-foreground/70 text-lg leading-relaxed" style={{
          maxWidth: "60ch"
        }}>
            Tre pilastri fondamentali della mia pratica medica, dedicati alla salute della donna, alla fertilità e alla consulenza medico-legale.
          </p>
        </div>

        <div className="space-y-24 lg:space-y-[120px]">
          {services.map((service, i) => <div key={i} data-index={i}>
              <ServiceBlock service={service} index={i} />
              {i < services.length - 1 && <div className="mt-24 lg:mt-[120px] flex items-center justify-center gap-6">
                  <div className="h-px w-24 sm:w-32" style={{
              backgroundColor: "var(--border)"
            }} />
                  <span className="inline-block h-1.5 w-1.5 rotate-45" style={{
              backgroundColor: "var(--primary)"
            }} aria-hidden="true" />
                  <div className="h-px w-24 sm:w-32" style={{
              backgroundColor: "var(--border)"
            }} />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}